import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  memo,
} from "react";
import { useDroppable } from "@dnd-kit/core";
import DesktopIcon from "@/components/desktop/DesktopIcon";
import DesktopFolder from "@/components/desktop/DesktopFolder";
import { FolderContentDialog } from "@/components/desktop/FolderContentDialog";
import DesktopGridOverlay from "@/components/desktop/DesktopGridOverlay";
import { useWindows } from "@/hooks/useWindows";
import { useUserApps } from "@/hooks/useUserApps";
import { useDesktopApps } from "@/hooks/useDesktopApps";
import { useFolders } from "@/hooks/useFolders";
import { useSettingsContext } from "@/contexts/SettingsContext";
import { useTranslation } from "react-i18next";
import { useDeepLink } from "@/hooks/useDeepLink";
import { useDesktopTour } from "@/hooks/useDesktopTour";
import { useIconGridPosition } from "@/hooks/useIconGridPosition";
import { TASKBAR_HEIGHT } from "@/shared/ui";
import type { WidgetSize } from "@/types/widget";
import { WIDGET_GRID_SIZE } from "@/types/widget";
import { useDesktopGrid } from "@/contexts/DesktopGridContext";
import { useGridDrag } from "@/contexts/GridDragContext";
import { THEME_PRESETS } from "@/constants/theme-options";
import {
  useWidgetDragMetadata,
  useWidgetDragMovement,
} from "@/contexts/WidgetDragContext";
import {
  EDGE_INSET_MAX,
  EDGE_INSET_RATIO,
  ICON_SIZE,
  makeCellKey,
} from "@/utils/desktop-layout-utils";
import { DESKTOP_ZONE_ID } from "@/components/widgets/WidgetDndProvider";
import { logWarn } from "@/lib/logger";

const DESKTOP_AREA_HEIGHT = `calc(100vh - ${TASKBAR_HEIGHT}px)`;
const DESKTOP_AREA_PADDING = 20;

// Custom event types for desktop icon management
type IconMovedEvent = CustomEvent<{ appId: string; position: { row: number; col: number } }>;
type IconPositionRequestEvent = CustomEvent<{ appId: string; callback: (pos: { row: number; col: number }) => void }>;

// Grid item type for desktop layout
type GridItem = {
  id: string;
  type: 'icon' | 'widget' | 'folder';
  row: number;
  col: number;
  rows: number;
  cols: number;
};

/**
 * DesktopDragPreview - Renders only during an active drag.
 * Subscribes to context internally to avoid parent re-renders on move.
 */
const DesktopDragPreview = memo(
  ({
    gridDimensions,
  }: {
    gridDimensions: {
      gridCellWidth: number;
      gridCellHeight: number;
      rows: number;
      cols: number;
      paddingX?: number;
      paddingY?: number;
    };
  }) => {
    const { activeItem } = useWidgetDragMetadata();
    const { currentDesktopDropPosition } = useWidgetDragMovement();

    const dragPreviewRect = useMemo(() => {
      if (!activeItem) return null;

      if (activeItem.source === "desktop") {
        return null;
      }

      if (activeItem.type === "widget" && activeItem.source === "sidebar") {
        return null;
      }

      if (!currentDesktopDropPosition) return null;

      const { gridCellWidth, gridCellHeight } = gridDimensions;

      if (activeItem.size === "icon") {
        const offsetX = (gridCellWidth - ICON_SIZE.width) / 2;
        const offsetY = (gridCellHeight - ICON_SIZE.height) / 2;
        return {
          x: currentDesktopDropPosition.col * gridCellWidth + offsetX,
          y: currentDesktopDropPosition.row * gridCellHeight + offsetY,
          width: ICON_SIZE.width,
          height: ICON_SIZE.height,
        };
      }

      const dims = WIDGET_GRID_SIZE[activeItem.size as WidgetSize];
      const areaWidth = dims.cols * gridCellWidth;
      const areaHeight = dims.rows * gridCellHeight;
      const insetX = Math.min(gridCellWidth * EDGE_INSET_RATIO, EDGE_INSET_MAX);
      const insetY = Math.min(gridCellHeight * EDGE_INSET_RATIO, EDGE_INSET_MAX);
      const width = Math.max(areaWidth - insetX * 2, areaWidth * 0.85);
      const height = Math.max(areaHeight - insetY * 2, areaHeight * 0.85);
      const offsetX = (areaWidth - width) / 2;
      const offsetY = (areaHeight - height) / 2;

      return {
        x: currentDesktopDropPosition.col * gridCellWidth + offsetX,
        y: currentDesktopDropPosition.row * gridCellHeight + offsetY,
        width,
        height,
      };
    }, [
      activeItem,
      currentDesktopDropPosition,
      gridDimensions,
    ]);

    if (!dragPreviewRect) {
      return null;
    }

    return (
      <div
        className="absolute rounded-3xl bg-black/4 shadow-[0_12px_24px_rgba(0,0,0,0.12)] transition-all duration-200 ease-out pointer-events-none z-0"
        style={{
          left: dragPreviewRect.x,
          top: dragPreviewRect.y,
          width: dragPreviewRect.width,
          height: dragPreviewRect.height,
        }}
      />
    );
  }
);
DesktopDragPreview.displayName = 'DesktopDragPreview';

/**
 * OptimizedDesktopGridOverlay - Subscribes to drag state internally to avoid re-rendering entire desktop.
 */
const OptimizedDesktopGridOverlay = memo(
  ({
    gridDimensions,
    occupiedCells,
  }: {
    gridDimensions: {
      rows: number;
      cols: number;
      gridCellWidth: number;
      gridCellHeight: number;
      paddingX: number;
      paddingY: number;
    };
    occupiedCells: Set<string>;
  }) => {
    const { activeItem } = useWidgetDragMetadata();
    const { isGridDragActive, hoveredGridPosition, draggedItemSize } =
      useGridDrag();

    const shouldShowGrid =
      isGridDragActive && activeItem?.type === "icon";

    return (
      <DesktopGridOverlay
        isActive={shouldShowGrid}
        gridDimensions={gridDimensions}
        occupiedCells={occupiedCells}
        hoveredPosition={hoveredGridPosition}
        hoveredSize={draggedItemSize || "icon"}
      />
    );
  }
);
OptimizedDesktopGridOverlay.displayName = 'OptimizedDesktopGridOverlay';

export function DesktopPage() {
  const { openWindow } = useWindows();
  const { settings, currentTheme } = useSettingsContext();
  const { t } = useTranslation();
  const { userApps, isLoading } = useUserApps();
  const {
    installedApps,
    isAppInstalled,
    isLoading: isLoadingDesktopApps,
    loadFailed: desktopAppsLoadFailed,
    reloadInstalledApps,
  } = useDesktopApps();
  const { folders, createFolder, addAppToFolder, renameFolder, deleteFolder, removeAppFromFolder, updateFolderPosition, isLoading: isLoadingFolders } = useFolders();
  const [iconWiggleTick, setIconWiggleTick] = useState(0);
  const [openFolderId, setOpenFolderId] = useState<string | null>(null);

  const iconsContainerRef = useRef<HTMLDivElement | null>(null);
  useDesktopTour({ iconsContainerRef, onIconWiggle: setIconWiggleTick });
  const [desktopAreaSize, setDesktopAreaSize] = useState({
    width: 0,
    height: 0,
  });

  // Set up desktop as droppable zone
  const { setNodeRef: setDesktopDroppableRef } = useDroppable({
    id: DESKTOP_ZONE_ID,
    data: { type: "widget-zone", zone: "desktop" },
  });

  // Filter apps based on settings and installation status - memoized to prevent infinite loop
  const filteredApps = useMemo(() => {
    if (isLoading || isLoadingDesktopApps || isLoadingFolders) {
      return [];
    }

    // Filter by installation status
    let apps = userApps.filter(({ id }) => isAppInstalled(id));

    // Filter out apps that are inside folders
    const appsInFolders = new Set<string>();
    Object.values(folders).forEach((folder) => {
      folder.appIds.forEach((appId) => appsInFolders.add(appId));
    });
    apps = apps.filter(({ id }) => !appsInFolders.has(id));

    return apps;
  }, [
    isLoading,
    isLoadingDesktopApps,
    isLoadingFolders,
    userApps,
    installedApps,
    folders, // Re-filter when folders change
    isAppInstalled,
  ]);

  // Get theme-based colors for icons
  const themeColors = useMemo(() => {
    if (currentTheme) {
      return {
        iconColor: currentTheme.icon_color,
        textColor: currentTheme.icon_text_color || "#FFFFFF",
      };
    }
    // Fallback to THEME_PRESETS for backwards compatibility
    const preset = THEME_PRESETS.light;
    return {
      iconColor: preset.iconColor,
      textColor: preset.textColor,
    };
  }, [currentTheme]);

  // Memoize app IDs to prevent infinite loop in useIconGridPosition
  const appIds = useMemo(
    () => filteredApps.map((app) => app.id),
    [filteredApps]
  );

  // Folders block one cell each
  const folderCells = useMemo(() => {
    const occupied = new Set<string>();
    Object.values(folders).forEach((folder) => {
      occupied.add(`${folder.position.row}:${folder.position.col}`);
    });
    return occupied;
  }, [folders]);

  // Grid position management
  const {
    positions: iconGridPositions, // Renamed for clarity
    getIconPosition,
    setIconPosition,
    gridToPixels,
    pixelsToGrid,
    gridDimensions,
  } = useIconGridPosition(appIds, desktopAreaSize, folderCells);

  const { activeItem } = useWidgetDragMetadata();

  // Drag-related state is now encapsulated in sub-components to avoid re-renders
  const gridOverlayStyle = useMemo(() => {
    const fillColor = "var(--desktop-grid-fill, rgba(255, 255, 255, 0.01))";
    return {
      backgroundColor: fillColor,
      borderRadius: "18px",
    };
  }, []);

  // Enable deep-linking
  useDeepLink();

  // Calculate minimum desktop area size to ensure at least 2x2 grid is visible
  const minDesktopSize = useMemo(() => {
    const minCols = 2;
    const minRows = 2;
    return {
      width: minCols * gridDimensions.gridCellWidth + 40, // +40 for padding
      height: minRows * gridDimensions.gridCellHeight + 40, // +40 for padding
    };
  }, [gridDimensions.gridCellWidth, gridDimensions.gridCellHeight]);

  // Memoize pixel positions for all icons to avoid unnecessary recalculations
  // This updates when positions or gridToPixels changes (e.g., on resize)
  const iconPixelPositions = useMemo(() => {
    const positions: Record<string, { x: number; y: number }> = {};
    filteredApps.forEach(({ id }) => {
      const gridPos = getIconPosition(id);
      positions[id] = gridPos ? gridToPixels(gridPos) : { x: 0, y: 0 };
    });
    return positions;
  }, [filteredApps, getIconPosition, gridToPixels]);

  // Pixel-Positionen für Ordner berechnen
  const folderPixelPositions = useMemo(() => {
    const positions: Record<string, { x: number; y: number }> = {};
    Object.entries(folders).forEach(([id, folder]) => {
      positions[id] = gridToPixels(folder.position);
    });
    return positions;
  }, [folders, gridToPixels]);

  const occupiedIconCells = useMemo(() => {
    const occupied = new Set<string>();
    Object.values(iconGridPositions).forEach((pos) => {
      occupied.add(makeCellKey(pos.row, pos.col));
    });
    return occupied;
  }, [iconGridPositions]);

  const allOccupiedCells = useMemo(() => {
    const occupied = new Set<string>(occupiedIconCells);
    folderCells.forEach((cell) => occupied.add(cell));

    // CRITICAL: Exclude the currently dragged item from occupancy
    if (activeItem) {
      if (activeItem.type === "icon") {
        // Find position in iconGridPositions for the dragged icon
        const iconPos = iconGridPositions[activeItem.id];
        if (iconPos) {
          occupied.delete(makeCellKey(iconPos.row, iconPos.col));
        }
      }
    }

    return occupied;
  }, [occupiedIconCells, folderCells, activeItem, iconGridPositions]);

  const { updateGridState } = useDesktopGrid();

  // Combine refs for desktop area (both container and droppable)
  const handleDesktopAreaRef = useCallback(
    (node: HTMLDivElement | null) => {
      iconsContainerRef.current = node;
      setDesktopDroppableRef(node);
    },
    [setDesktopDroppableRef]
  );

  // Handle unified icon movements from dnd-kit
  useEffect(() => {
    const handleIconMoved = (e: Event) => {
      const { appId, position } = (e as IconMovedEvent).detail;
      setIconPosition(appId, position);
    };
    const handleRequestIconPosition = (e: Event) => {
      const { appId, callback } = (e as IconPositionRequestEvent).detail;
      const pos = getIconPosition(appId);
      if (pos) callback(pos);
    };
    window.addEventListener("desktop-icon-moved", handleIconMoved);
    window.addEventListener("request-icon-position", handleRequestIconPosition);
    return () => {
      window.removeEventListener("desktop-icon-moved", handleIconMoved);
      window.removeEventListener(
        "request-icon-position",
        handleRequestIconPosition
      );
    };
  }, [setIconPosition, getIconPosition]);

  // Handle folder creation / add-to-folder via drag-and-drop
  useEffect(() => {
    const handleCreateFolder = (e: Event) => {
      const { draggedAppId, targetAppId, position } = (e as CustomEvent<{ draggedAppId: string; targetAppId: string; position: { row: number; col: number } }>).detail;
      createFolder([draggedAppId, targetAppId], position).catch(() =>
        logWarn('Fehler beim Erstellen des Ordners via DnD')
      );
    };
    const handleAddToFolder = (e: Event) => {
      const { draggedAppId, folderId } = (e as CustomEvent<{ draggedAppId: string; folderId: string }>).detail;
      addAppToFolder(folderId, draggedAppId).catch(() =>
        logWarn('Fehler beim Hinzufügen zum Ordner via DnD')
      );
    };
    window.addEventListener("desktop-icon-create-folder", handleCreateFolder);
    window.addEventListener("desktop-icon-add-to-folder", handleAddToFolder);
    return () => {
      window.removeEventListener("desktop-icon-create-folder", handleCreateFolder);
      window.removeEventListener("desktop-icon-add-to-folder", handleAddToFolder);
    };
  }, [createFolder, addAppToFolder]);

  useEffect(() => {
    const node = iconsContainerRef.current;
    if (!node || !gridDimensions.gridCellWidth) {
      updateGridState(null);
      return;
    }

    const rect = node.getBoundingClientRect();

    // Registry of all items with their grid info
    const items: GridItem[] = [];
    Object.entries(iconGridPositions).forEach(([id, pos]) => {
      items.push({ id, type: "icon", ...pos, rows: 1, cols: 1 });
    });
    Object.entries(folders).forEach(([id, folder]) => {
      items.push({ id, type: "folder", ...folder.position, rows: 1, cols: 1 });
    });

    updateGridState({
      offsetLeft: rect.left,
      offsetTop: rect.top,
      cellWidth: gridDimensions.gridCellWidth,
      cellHeight: gridDimensions.gridCellHeight,
      cols: gridDimensions.cols,
      rows: gridDimensions.rows,
      paddingX: DESKTOP_AREA_PADDING,
      paddingY: DESKTOP_AREA_PADDING,
      items,
      occupiedCells: new Set(allOccupiedCells),
    });
  }, [
    gridDimensions,
    iconGridPositions,
    allOccupiedCells,
    folders,
    updateGridState,
  ]);

  useEffect(() => () => updateGridState(null), [updateGridState]);


  useLayoutEffect(() => {
    const element = iconsContainerRef.current;
    if (!element) {
      return;
    }

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      setDesktopAreaSize({
        width: rect.width,
        height: rect.height,
      });
    };

    updateSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateSize);
      return () => {
        window.removeEventListener("resize", updateSize);
      };
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      setDesktopAreaSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  // All DnD logic is now handled centrally in WidgetDndProvider
  // No need for custom event listeners or manual drag handling here

  return (
    <div className="h-full w-full overflow-hidden">
      <div
        className="w-full overflow-auto"
        style={{
          height: DESKTOP_AREA_HEIGHT,
          minWidth: `${minDesktopSize.width}px`,
          minHeight: `${minDesktopSize.height}px`,
        }}
      >
        <div
          className="flex w-full justify-start items-start"
          style={{
            minHeight: DESKTOP_AREA_HEIGHT,
            minWidth: `${minDesktopSize.width}px`,
          }}
        >
          <div
            className="relative"
            style={{
              ...gridOverlayStyle,
              padding: `${DESKTOP_AREA_PADDING}px`,
              width: "100%",
              height: DESKTOP_AREA_HEIGHT,
              minHeight: `${minDesktopSize.height}px`,
              minWidth: `${minDesktopSize.width}px`,
              overflow: "hidden",
            }}
            ref={handleDesktopAreaRef}
            data-driver-id="desktop-icons-area"
          >
            {filteredApps.map((app) => {
                const label = t(`apps.${app.id}`);
                const pixelPos = iconPixelPositions[app.id] || { x: 0, y: 0 };

                return (
                  <DesktopIcon
                    key={app.id}
                    appId={app.id}
                    label={label}
                    Icon={app.Icon}
                    showIcon={settings.desktopIcons.showIcons}
                    showLabel={settings.desktopIcons.showLabels}
                    iconColor={themeColors.iconColor}
                    textColor={themeColors.textColor}
                    wiggleTrigger={iconWiggleTick}
                    position={pixelPos}
                    onActivate={() => openWindow(app.id, label)}
                  />
                );
              })}

            {/* Desktop Folders */}
            {Object.entries(folders).map(([folderId, folder]) => {
                const pixelPos = folderPixelPositions[folderId] || { x: 0, y: 0 };
                return (
                  <DesktopFolder
                    key={folderId}
                    folderId={folderId}
                    folder={folder}
                    onOpen={() => setOpenFolderId(folderId)}
                    showIcon={settings.desktopIcons.showIcons}
                    showLabel={settings.desktopIcons.showLabels}
                    iconColor={themeColors.iconColor}
                    textColor={themeColors.textColor}
                    wiggleTrigger={iconWiggleTick}
                    position={pixelPos}
                    gridToPixels={gridToPixels}
                    pixelsToGrid={pixelsToGrid}
                    dragConstraints={iconsContainerRef}
                    onDragEnd={(gridPos) => updateFolderPosition(folderId, gridPos)}
                  />
                );
              })}

            {/* Grid Overlay - Subscribes internally to avoid parent re-render */}
            <OptimizedDesktopGridOverlay
              gridDimensions={{
                ...gridDimensions,
                paddingX: DESKTOP_AREA_PADDING,
                paddingY: DESKTOP_AREA_PADDING,
              }}
              occupiedCells={allOccupiedCells}
            />

            {/* Drag Preview - Subscribes internally to avoid parent re-render */}
            <DesktopDragPreview
              gridDimensions={{
                ...gridDimensions,
                paddingX: DESKTOP_AREA_PADDING,
                paddingY: DESKTOP_AREA_PADDING,
              }}
            />

            {isLoading && (
              <div className="flex items-center justify-center w-full h-32 text-white">
                <span>{t('desktop.loadingApps')}</span>
              </div>
            )}

            {desktopAppsLoadFailed && !isLoadingDesktopApps && (
              <div
                role="alert"
                className="flex items-center justify-center gap-3 w-full h-32 text-white"
              >
                <span>{t('desktop.loadAppsFailed')}</span>
                <button
                  type="button"
                  onClick={() => void reloadInstalledApps()}
                  className="rounded-md border border-white/60 px-3 py-1 text-sm hover:bg-white/10"
                >
                  {t('common.retry')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Folder Content Dialog */}
      <FolderContentDialog
        isOpen={openFolderId !== null}
        folderId={openFolderId || ''}
        folder={openFolderId ? folders[openFolderId] : null}
        onClose={() => setOpenFolderId(null)}
        onRename={async (newName) => {
          if (openFolderId) {
            try {
              await renameFolder(openFolderId, newName);
            } catch (error) {
              console.error('Fehler beim Umbenennen des Ordners:', error);
            }
          }
        }}
        onRemoveApp={async (appId) => {
          if (openFolderId) {
            try {
              await removeAppFromFolder(openFolderId, appId);
            } catch (error) {
              console.error('Fehler beim Entfernen der App:', error);
            }
          }
        }}
        onDeleteFolder={async () => {
          if (openFolderId) {
            try {
              await deleteFolder(openFolderId);
              setOpenFolderId(null);
            } catch (error) {
              console.error('Fehler beim Löschen des Ordners:', error);
            }
          }
        }}
        onOpenApp={(appId) => {
          const label = t(`apps.${appId}`);
          openWindow(appId, label);
          setOpenFolderId(null);
        }}
      />
    </div>
  );
}
